import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Chip,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { Warning, Info, MenuBook, AccountTree, CheckCircle } from '@mui/icons-material';
import { useEmbedded } from '../../hooks/useEmbedded';

const SectionPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3), 
}));

const PrerequisiteCard = styled(Box)(({ theme }) => ({
  display: 'flex',
  height: '100%',
  border: `1px solid #bbb`,
  borderRadius: '8px',
  overflow: 'hidden',
}));

const LearningPathPrerequisites = ({ prerequisites, parentLPathId, parentLPathName }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isEmbedded = useEmbedded();
  const [imageErrors, setImageErrors] = useState({});
  
  // Get the current learning path ID from the URL path
  const currentLPathId = location.pathname.split('/').pop();
  
  const handleImageError = (prerequisiteId) => {
      setImageErrors(prev => ({
      ...prev,
      [prerequisiteId]: true
      }));
  };  

  const getStatusIcon = (prerequisite) => {
    // If the prerequisite is completed, show a check icon
    if (prerequisite.status === '1') {
      return <CheckCircle color="success" />;
    }
    
    // Otherwise, use the existing logic
    if (!prerequisite.status) {
      return prerequisite.required === '1' ? <Warning color="warning" /> : <Info color="disabled" />;
    }    
    switch (prerequisite.required) {
      case '0': return <Info color="info" />;
      case '1': return <Warning color="error" />;
      default: return <Info color="info" />;
    }
  };
  
  const getStatusColor = (prerequisite) => {
    // If the prerequisite is completed, show success color
    if (prerequisite.status === '1') {
      return 'success';
    }
    
    // Otherwise, use the existing logic
    if (!prerequisite.status) {
      return prerequisite.required === '1' ? 'warning' : 'default';
    }
    
    switch (prerequisite.required) {
      case '1': return 'success';
      case '2': return 'info';
      default: return 'error';
    }
  };
  
  const getStatusLabel = (prerequisite) => {
    // If the prerequisite is completed, show "Completed"
    if (prerequisite.status === '1') {
      return 'Completed';
    }
    
    // Otherwise, show "Required" or "Optional" based on the required field
    return prerequisite.required === '1' ? 'Required' : 'Optional';
  };

  const getContentIcon = (type) => {
      switch (type?.toLowerCase()) {
      case 'course':
          return <MenuBook fontSize="large" color="primary" />;
      case 'learning path':
          return <AccountTree fontSize="large" color="blueShade" />;
      default:
          return <MenuBook fontSize="large" color="primary" />;
      }
  };

  return (
    <SectionPaper elevation={0}>
      <Typography variant="h5" component="h2" gutterBottom>
        Prerequisites
      </Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        The following items are prerequisites for this learning path.
      </Typography>
      
      <Grid container spacing={2}>
        {prerequisites.prerequisites && prerequisites.prerequisites.map((prerequisite) => {
          const hasImageError = imageErrors[prerequisite.id];

          const coverImage = prerequisite.cover_picture 
            ? `/uploads/${prerequisite.prerequisite_type === 'course' ? 'courses' : 'learning_paths'}/${prerequisite.cover_picture}`
            : `/assets/images/course-default.jpg`;
          return (
            <Grid item xs={12} sm={12} key={prerequisite.id}>
              <PrerequisiteCard
                className="prerequisite-card"
                onClick={() => {
                  // Add embedded path prefix if in embedded mode
                  const pathPrefix = isEmbedded ? '/embed' : '';
                  const targetPath = prerequisite.prerequisite_type === 'course' 
                    ? `${pathPrefix}/learner/course/${prerequisite.prerequisite_id}`
                    : `${pathPrefix}/learner/lpath/${prerequisite.prerequisite_id}`;
                    
                  navigate(targetPath, {
                    state: {
                      parentLPathId: parentLPathId || currentLPathId,
                      parentLPathName: parentLPathName || document.title || "Parent Learning Path"
                    }
                  });
                }}
              >
                <Box className="card-image-wrapper">
                    {hasImageError ? (
                        <Box 
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '100%',
                            height: '100%',
                            backgroundColor: prerequisite.prerequisite_type === 'course' ? '#1976d220' : '#01436120'
                        }}
                        >
                        {getContentIcon(prerequisite.prerequisite_type)}
                        </Box>
                    ) : (
                        <img
                        className="card-image"
                        src={coverImage}
                        alt={prerequisite.prerequisite_name}
                        onError={() => handleImageError(prerequisite.id)}
                        />
                    )}
                </Box>
                <Box className="card-content" sx={{ padding: '0 16px'}}>                  
                  <Chip 
                    icon={getStatusIcon(prerequisite)}
                    label={getStatusLabel(prerequisite)}
                    size="small"
                    color={getStatusColor(prerequisite)}
                    className="requirement-chip"
                  />
                  <Typography variant="subtitle1" className="card-title">
                    {prerequisite.prerequisite_name}
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block'}}>
                        {prerequisite.prerequisite_type === 'course' ? 'Course' : 'Learning Path'}
                    </Typography>
                  </Typography>                  
                  <Typography variant="body2" className="prerequisite-description" 
                    dangerouslySetInnerHTML={{ __html: prerequisite.description || '' }}
                  />
                </Box>
              </PrerequisiteCard>
            </Grid>
          );
        })}
      </Grid>
    </SectionPaper>
  );
};

export default LearningPathPrerequisites;